// frontend/src/components/purchase-orders/ChannelProjection.jsx
//
// A purchase order's Shopify projection, on the SaaS PO details page:
//
//   <ProjectionPanel po onProject />  — for failed and diverged: the issues
//                                       and the action the state allows.
//                                       Renders nothing for any other state.
//
// Which action a state gets, and how an issue reads, is
// `utils/channelProjection.js`'s call.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';

import {
  PROJECTION_FAILED, PROJECTION_DIVERGED, isProjectionException, projectionAction,
  projectionIssueText,
} from '../../utils/channelProjection';

/**
 * `po` is the DETAIL wire shape (PurchaseOrderHeaderDetailSerializer): the
 * issues, `channel_projection_issues`, are served on the retrieve endpoint
 * only, never per row on the list.
 *
 * `onProject(po)` is the page's handler (alerts + cache invalidation, never
 * throws). The panel tracks its own in-flight state around it so the button
 * cannot be pressed twice while the projection re-runs.
 */
export function ProjectionPanel({ po, onProject }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const status = po.channel_projection_status;
  if (!isProjectionException(status)) return null;

  const issues = po.channel_projection_issues;
  const action = projectionAction(status, issues);
  const severity = status === PROJECTION_FAILED ? 'error' : 'warning';
  const project = async () => {
    setBusy(true);
    try {
      await onProject(po);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Alert severity={severity} sx={{ mb: 2 }}>
      <Stack spacing={1} alignItems="flex-start">
        <Typography variant="body2">
          {status === PROJECTION_FAILED
            ? t('purchaseOrders.projection.failedIntro')
            : t('purchaseOrders.projection.divergedIntro')}
        </Typography>
        {issues.length > 0 ? (
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {issues.map((issue, index) => (
              <Typography component="li" variant="body2" key={index}>
                {projectionIssueText(issue, t)}
              </Typography>
            ))}
          </Box>
        ) : (
          // A failed status with no issues is a projection that never
          // recorded its outcome (backend channel_sync._claim_projection).
          <Typography variant="body2">{t('purchaseOrders.projection.unfinished')}</Typography>
        )}
        {action === 'reproject' && (
          <Typography variant="body2">{t('purchaseOrders.projection.transferMissing')}</Typography>
        )}
        {status === PROJECTION_DIVERGED && action === null && (
          <Typography variant="body2">{t('purchaseOrders.projection.divergedNoAction')}</Typography>
        )}
        {action && (
          <Button variant="outlined" size="small" loading={busy} onClick={project}>
            {action === 'retry'
              ? t('purchaseOrders.projection.retry')
              : t('purchaseOrders.projection.reproject')}
          </Button>
        )}
      </Stack>
    </Alert>
  );
}
